import { Divider } from '@material-ui/core';
import React, { Dispatch, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import STATIC_TEXT from '../../../content/common.json';
import useApplicantInfoStyles from './applicant-info.styles';
import PRO_TIP_ICON from '../../../assets/pro-tip-welcomescreen.svg';
import { WelcomeFooterWrapper } from '../welcome-footer-wrapper';
import { AcknowledgeTerms } from './acknowledge-terms';
import { useEntityState } from '../../../stores/entity/entity.hooks';
import { getFormDataValuesObjectForDraftApi } from '../../custom/forms/form-helper-methods';
import { updateEntityDraft } from '../../../services/fenergo-service';
import { useDispatch } from 'react-redux';
import { setSnackBar } from '../../../stores/ui/ui-action';
import CONSTANTS from '../../../utils/constants';
import CustomLoading from '../../custom/custom-loading';

interface ApplicantInfoFormData {
  legalEntityName: string;
  companyWebsite: string;
  acknowledgeTCPP: boolean;
}

export const ApplicantInfo: React.FC = () => {
  const { letsTalk } = useApplicantInfoStyles();

  const [isEdit, setIsEdit] = useState(false);
  const [readTerms, setReadTerms] = useState(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const dispatch: Dispatch<any> = useDispatch();

  const {
    entityDraft,
    selectedEntity,
    selectedJourneyTask,
    entityDraftLoadInProgress
  } = useEntityState();

  const defaultValues: Partial<ApplicantInfoFormData> = {
    legalEntityName: '',
    companyWebsite: '',
    acknowledgeTCPP: false
  };

  const [values, setValues] = useState(defaultValues);
  const [originalValues, setOriginalValues] = useState(defaultValues);

  const navigate = useNavigate();

  useEffect(() => {
    if (!entityDraftLoadInProgress && entityDraft && entityDraft.properties) {
      const entityProps = entityDraft.properties;

      const _values: Partial<ApplicantInfoFormData> = {
        legalEntityName: entityProps.legalEntityName?.value || '',
        companyWebsite: entityProps.companyWebsite?.value || '',
        acknowledgeTCPP: entityProps.acknowledgeTCPP?.value === 'Yes'
      };

      setValues(_values);
      setOriginalValues(_values);
      setReadTerms(_values.acknowledgeTCPP);
    }
  }, [entityDraft, entityDraftLoadInProgress])

  const onChange = (e): void => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const onAcknowledged = (e): void => {
    setReadTerms(e.target.checked);
    setValues({ ...values, acknowledgeTCPP: e.target.checked });
  };

  const onNext = (): void => {
    if (readTerms) {
      onSave(readTerms);
    }
  };

  const onCancel = (): void => {
    setIsEdit(!isEdit);
    setValues(originalValues);
  };

  const updateEntityDraftForTask = async (entityId, entityDraftId, formData) => {
    try {
      await updateEntityDraft(entityId, entityDraftId, formData);
      return true;
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
      setSubmitting(false);
      return false;
    }
  }

  const onSave = async (readTerms = false) => {
    setSubmitting(true);
    var formData = getFormDataValuesObjectForDraftApi(CONSTANTS.CLIENT, { acknowledgeTCPP: values.acknowledgeTCPP }, entityDraft.jurisdictions, 'Company', ['Applicant Information']);

    var isSuccess = await updateEntityDraftForTask(selectedEntity.id, entityDraft.id, { data: formData });

    if (isSuccess) {
      setOriginalValues(values);
      setIsEdit(!isEdit);
    }

    setSubmitting(false);
    if(readTerms){
      navigate(`/journey/${selectedEntity.journeys[0].journeyId}/task/${selectedJourneyTask.id}`);
    }
  };

  const getProTipDescription = () => (
    <>
      {STATIC_TEXT.welcome.applicationInfo.proTipDescription1}
      <strong>{STATIC_TEXT.welcome.applicationInfo.proTipDescription2}</strong>
      {STATIC_TEXT.welcome.applicationInfo.proTipDescription3}
      <a href="https://start.svb.com/login" target="_blank" rel="noopener noreferrer">{STATIC_TEXT.welcome.applicationInfo.proTipDescription4}</a>
      .
    </>
  );

  return (
    <>
      {/* <LoadingOnBackdrop status={submitting} message="Saving data" /> */}
      {submitting && <CustomLoading message="Saving Data..." />}
      <div className={letsTalk}>
        <p className='infoHeading'>Great! Let’s Talk Lending</p>
        <p className='infoText'>We are going to need to collect some information about your company. </p>
        <ul className='listItems'>
          <li>This will take approximately 5-10 mins</li>
          <li>We’ll need you to connect your accounting platform</li>
          <li>Be prepared to upload a detailed cap table</li>
        </ul>
      </div>
      <div className={`footer__content ${isEdit ? 'disabled__content' : ''}`}>

        <AcknowledgeTerms readTerms={readTerms} onChange={onAcknowledged}></AcknowledgeTerms>

        <Divider />

        <WelcomeFooterWrapper
          proTipIcon={PRO_TIP_ICON}
          proTipLabel={STATIC_TEXT.welcome.applicationInfo.proTipLabel}
          proTipDescription={getProTipDescription()}
          buttonLabel={STATIC_TEXT.welcome.navigationButton.next_welcomescreen_as_continue}
          onNextClick={onNext}
          hideBackButton
          disabled={isEdit || !readTerms}
        />
      </div>
    </>
  );
};
